// src/components/ContextToolbar.tsx
/**
 * Context-Specific Toolbar Component
 * 
 * Shows tool-specific interfaces based on active tool mode:
 * - Jump mode: Jump creation and management tools
 * - Measurement mode: Measurement tools and controls
 * - Annotation mode: Drawing tools, colors, thickness
 * - Expression mode: Expression editor and variables
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, SubdivisionMode } from '../types/redux';
import { selectActiveTool } from '../store/slices/toolModeSlice';
import { Settings, ChevronDown, Plus, X, AlertTriangle } from 'lucide-react';
import AnnotationToolbar from './AnnotationToolbar';
import { 
  addJump,
  deleteJump,
  duplicateJump,
  clearJumps,
  deselectAllJumps,
  deleteSelectedJumps,
  moveSelectedJumpsToLine,
  updateLayoutSettings,
  reverseJump
} from '../store/slices/jumpIndicatorsSlice';
import { clearAllMeasurements, deleteSelectedMeasurements, cancelMeasurement, openAddPointDialog } from '../store/slices/uiSlice';
import { 
  setEnvironmentVariable, 
  updateEnvironment,
  clearAllExpressions,
  deleteSelectedExpressionObjects,
  selectExpressionObjects,
  selectExpressionEnvironment,
  selectVariablesInUse,
  selectSelectedExpressionObjects,
  showExpressionWidget
} from '../store/slices/expressionsSlice';
import { parseFraction, toNumber, formatFractionForMath } from '../lib/fractions';
import { type Fraction } from '../types/redux';
import MathRenderer from './MathRenderer';
import { centerOrigin, pan } from '../store/slices/viewportSlice';
import { toggleAnnotationLayerPanning, toggleMainLabelsHidden, setTickSubdivisionMode, setShowEquivalentIntegerLine } from '../store/slices/uiSlice';

// Helper function to detect if input looks like a fraction
const detectInputFormat = (input: string): 'fraction' | 'decimal' | 'integer' => {
  const trimmed = input.trim();
  
  // Check if it contains a slash (fraction format)
  if (trimmed.includes('/')) {
    return 'fraction';
  }
  
  // Check if it contains a decimal point
  if (trimmed.includes('.')) {
    return 'decimal';
  }
  
  // Default to integer
  return 'integer';
};

// Helper function to render value based on original format
const renderVariableValue = (value: Fraction, originalInput: string): string => {
  const format = detectInputFormat(originalInput);
  
  switch (format) {
    case 'fraction':
      // Use KaTeX formatting for fractions
      return formatFractionForMath(value);
    case 'decimal':
      return toNumber(value).toString();
    case 'integer':
      // Check if the result is actually an integer
      const numValue = toNumber(value);
      return Number.isInteger(numValue) ? numValue.toString() : toNumber(value).toString();
    default:
      return toNumber(value).toString();
  }
};

// Variables Modal Component
const VariablesModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const environment = useSelector(selectExpressionEnvironment);
  const variablesInUse = useSelector(selectVariablesInUse);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');
  const [variableInputValues, setVariableInputValues] = useState<Record<string, string>>({});

  // Initialize input values when environment changes
  useEffect(() => {
    const newInputValues: Record<string, string> = {};
    Object.entries(environment).forEach(([name, value]) => {
      if (!(name in variableInputValues)) {
        // Only initialize if not already set (to preserve user input)
        newInputValues[name] = toNumber(value as Fraction).toString();
      }
    });
    
    if (Object.keys(newInputValues).length > 0) {
      setVariableInputValues(prev => ({
        ...prev,
        ...newInputValues
      }));
    }
  }, [environment]);

  const handleVariableChange = (variable: string, value: string) => {
    // Update the input value state
    setVariableInputValues(prev => ({
      ...prev,
      [variable]: value
    }));

    // Only try to parse and update environment if we have a non-empty value
    if (value.trim()) {
      try {
        const fraction = parseFraction(value);
        dispatch(setEnvironmentVariable({ name: variable, value: fraction }));
      } catch (error) {
        // Don't update environment if parsing fails, but keep the input value
        console.warn('Invalid fraction:', value);
      }
    }
    // If value is empty, don't update the environment - let the user finish typing
  };

  const handleAddVariable = () => {
    if (newVariableName && newVariableValue) {
      try {
        const fraction = parseFraction(newVariableValue);
        dispatch(setEnvironmentVariable({ name: newVariableName, value: fraction }));
        
        // Update input values state
        setVariableInputValues(prev => ({
          ...prev,
          [newVariableName]: newVariableValue
        }));
        
        setNewVariableName('');
        setNewVariableValue('');
      } catch (error) {
        alert('Invalid fraction format. Use format like: 3, 1/2, 2/3, etc.');
      }
    }
  };

  const handleDeleteVariable = (variableName: string) => {
    // Remove from environment by setting to undefined
    dispatch(setEnvironmentVariable({ name: variableName, value: undefined }));
    
    // Remove from input values
    setVariableInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[variableName];
      return newValues;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Variable Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Variables */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Current Variables ({Object.keys(environment).length})
            </h3>
            
            {Object.keys(environment).length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">No variables defined</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(environment).map(([name, value]) => {
                  const inputValue = name in variableInputValues ? variableInputValues[name] : toNumber(value as Fraction).toString();
                  const displayValue = renderVariableValue(value as Fraction, inputValue);
                  
                  return (
                    <div key={name} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="font-mono text-sm w-12 text-gray-700 dark:text-gray-300 flex items-center">
                        <MathRenderer expression={name} />
                        <span className="ml-1">=</span>
                      </div>
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => handleVariableChange(name, e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        placeholder="e.g., 3, 1/2, 0.5"
                      />
                      <div className="text-sm text-gray-500 dark:text-gray-400 w-16 flex items-center justify-center">
                        <MathRenderer expression={displayValue} />
                      </div>
                      <button
                        onClick={() => handleDeleteVariable(name)}
                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Variable */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Add New Variable</h3>
            
            <div className="space-y-3">
              <input
                type="text"
                value={newVariableName}
                onChange={(e) => setNewVariableName(e.target.value)}
                placeholder="Variable name (e.g., x, y, a, b)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <input
                type="text"
                value={newVariableValue}
                onChange={(e) => setNewVariableValue(e.target.value)}
                placeholder="Value (e.g., 3, 1/2, 0.5, 2.75)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <button
                onClick={handleAddVariable}
                disabled={!newVariableName || !newVariableValue}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Add Variable
              </button>
            </div>
          </div>

          {/* Variables in Use */}
          {variablesInUse.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Variables in Use</h3>
              <div className="flex flex-wrap gap-2">
                {variablesInUse.map((variable) => (
                  <span
                    key={variable}
                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-sm font-mono flex items-center"
                  >
                    <MathRenderer expression={variable} />
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">How to use variables:</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Variables can be used in expressions like <MathRenderer expression="x + 2" /> or <MathRenderer expression="a * b" /></li>
              <li>• Values can be integers (3), fractions (<MathRenderer expression="1/2" />), or decimals (0.5)</li>
              <li>• Variable names should be single letters or short words</li>
              <li>• Changes to variables automatically update all expressions that use them</li>
              <li>• Implicit multiplication is supported: <MathRenderer expression="2a" /> means <MathRenderer expression="2 * a" /></li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Jump Toolbar Component
const JumpToolbar: React.FC = () => {
  const dispatch = useDispatch();
  const jumpIndicators = useSelector((state: RootState) => state.jumpIndicators || { jumps: [] });
  const layoutSettings = useSelector((state: RootState) => state.jumpIndicators.layoutSettings);
  const selectedJumpsCount = jumpIndicators.jumps?.filter(j => j.selected).length || 0;
  const [quickStartValue, setQuickStartValue] = useState('0');
  const [quickEndValue, setQuickEndValue] = useState('3');
  const [targetLine, setTargetLine] = useState<'primary' | 'secondary' | 'both'>('primary');
  const [showDisplayOptions, setShowDisplayOptions] = useState(false);

  const handleQuickCreateJump = () => {
    const start = parseFloat(quickStartValue);
    const end = parseFloat(quickEndValue);
    
    if (!isNaN(start) && !isNaN(end)) {
      if (targetLine === 'both') {
        // Create jump on both lines
        dispatch(addJump({ startValue: start, endValue: end, line: 'primary' }));
        dispatch(addJump({ startValue: start, endValue: end, line: 'secondary' }));
      } else {
        // Create jump on selected line
        dispatch(addJump({ startValue: start, endValue: end, line: targetLine }));
      }
    }
  };

  const handleDeleteSelected = () => {
    const selectedJumps = jumpIndicators.jumps.filter(j => j.selected);
    selectedJumps.forEach(jump => {
      dispatch(deleteJump(jump.id));
    });
  };

  const handleDuplicateSelected = () => {
    const selectedJumps = jumpIndicators.jumps.filter(j => j.selected);
    selectedJumps.forEach(jump => {
      dispatch(duplicateJump(jump.id));
    });
  };

  const handleMoveToBoth = () => {
    const selectedJumps = jumpIndicators.jumps.filter(j => j.selected);
    // For each selected jump, create a duplicate on the opposite line
    selectedJumps.forEach(jump => {
      const targetLine = jump.line === 'primary' ? 'secondary' : 'primary';
      dispatch(addJump({ 
        startValue: jump.startValue, 
        endValue: jump.endValue, 
        line: targetLine,
        label: jump.label 
      }));
    });
  };

  const handleJumpTypeChange = (type: 'additive' | 'multiplicative') => {
    dispatch(updateLayoutSettings({ jumpType: type }));
  };

  const handleScaleFactorFormatChange = (format: 'SF=' | '×') => {
    dispatch(updateLayoutSettings({ scaleFactorFormat: format }));
  };

  const handleShowCalculationChange = () => {
    dispatch(updateLayoutSettings({ showCalculation: !layoutSettings.showCalculation }));
  };

  const handleShowResultChange = () => {
    dispatch(updateLayoutSettings({ showResult: !layoutSettings.showResult }));
  };

  const handleAlwaysShowSignsChange = () => {
    dispatch(updateLayoutSettings({ alwaysShowSigns: !layoutSettings.alwaysShowSigns }));
  };

  const handleShowJumpValueChange = () => {
    dispatch(updateLayoutSettings({ showJumpValue: !layoutSettings.showJumpValue }));
  };

  // Check if any jumps start from zero (multiplicative mode won't work for those)
  const hasZeroStartJumps = jumpIndicators.jumps.some(j => Math.abs(j.startValue) < 1e-10);

  // Close dropdown when clicking outside
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDisplayOptions(false);
      }
    };

    if (showDisplayOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDisplayOptions]);

  return (
    <div className="context-toolbar p-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* Left side: Title and count */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M13 7l5 5-5 5M6 12h12" />
            </svg>
            <span className="font-medium text-sm text-blue-900 dark:text-blue-100">Jump Indicators</span>
          </div>
          
          <div className="text-xs text-blue-700 dark:text-blue-300 whitespace-nowrap">
            {jumpIndicators.jumps?.length || 0} total
            {' • '}
            {jumpIndicators.jumps?.filter(j => j.line === 'primary').length || 0} prim
            {' • '}
            {jumpIndicators.jumps?.filter(j => j.line === 'secondary').length || 0} sec
            {selectedJumpsCount > 0 && ` • ${selectedJumpsCount} sel`}
          </div>
        </div>

        {/* Center: Quick creation */}
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="flex items-center gap-0.5">
            <label className="text-xs text-blue-700 dark:text-blue-300">Start:</label>
            <input
              type="number"
              value={quickStartValue}
              onChange={(e) => setQuickStartValue(e.target.value)}
              className="w-14 px-1.5 py-0.5 text-xs border border-blue-300 rounded bg-white dark:bg-gray-800"
              step="0.5"
            />
          </div>
          <div className="flex items-center gap-0.5">
            <label className="text-xs text-blue-700 dark:text-blue-300">End:</label>
            <input
              type="number"
              value={quickEndValue}
              onChange={(e) => setQuickEndValue(e.target.value)}
              className="w-14 px-1.5 py-0.5 text-xs border border-blue-300 rounded bg-white dark:bg-gray-800"
              step="0.5"
            />
          </div>
          <div className="flex items-center gap-0.5">
            <label className="text-xs text-blue-700 dark:text-blue-300">Line:</label>
            <select
              value={targetLine}
              onChange={(e) => setTargetLine(e.target.value as 'primary' | 'secondary' | 'both')}
              className="px-1.5 py-0.5 text-xs border border-blue-300 rounded bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="both">Both</option>
            </select>
          </div>
          <button
            onClick={handleQuickCreateJump}
            className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            Create
          </button>
        </div>

        {/* Right side: Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Display Options Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDisplayOptions(!showDisplayOptions)}
              className="px-2 py-0.5 text-xs bg-violet-500 text-white rounded hover:bg-violet-600 transition-colors whitespace-nowrap flex items-center gap-1"
              title="Jump display options"
            >
              <Settings size={12} />
              <span className="hidden sm:inline">Display</span>
              <ChevronDown size={10} className={`transition-transform ${showDisplayOptions ? 'rotate-180' : ''}`} />
            </button>
            
            {showDisplayOptions && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3">
                <div className="space-y-3">
                  <div className="font-medium text-xs text-gray-700 dark:text-gray-300 mb-2">Jump Display Options</div>
                  
                  {/* Jump Type */}
                  <div className="space-y-1">
                    <label className="text-xs text-gray-600 dark:text-gray-400">Jump Type</label>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleJumpTypeChange('additive')}
                        className={`flex-1 px-2 py-1 text-xs rounded border flex items-center justify-center gap-1 ${
                          layoutSettings.jumpType === 'additive'
                            ? 'bg-violet-500 text-white border-violet-500'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Plus size={10} />
                        Additive
                      </button>
                      <button
                        onClick={() => handleJumpTypeChange('multiplicative')}
                        className={`flex-1 px-2 py-1 text-xs rounded border flex items-center justify-center gap-1 ${
                          layoutSettings.jumpType === 'multiplicative'
                            ? 'bg-violet-500 text-white border-violet-500'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <X size={10} />
                        Multiplicative
                      </button>
                    </div>
                    {layoutSettings.jumpType === 'multiplicative' && hasZeroStartJumps && (
                      <div className="flex items-start gap-1 text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-1.5 rounded">
                        <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
                        <span>Some jumps start from zero and will show as additive</span>
                      </div>
                    )}
                  </div>

                  {/* Scale Factor Format (only in multiplicative mode) */}
                  {layoutSettings.jumpType === 'multiplicative' && (
                    <div className="space-y-1">
                      <label className="text-xs text-gray-600 dark:text-gray-400">Scale Factor Format</label>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleScaleFactorFormatChange('SF=')}
                          className={`flex-1 px-2 py-1 text-xs rounded border ${
                            layoutSettings.scaleFactorFormat === 'SF='
                              ? 'bg-violet-500 text-white border-violet-500'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          SF=
                        </button>
                        <button
                          onClick={() => handleScaleFactorFormatChange('×')}
                          className={`flex-1 px-2 py-1 text-xs rounded border ${
                            layoutSettings.scaleFactorFormat === '×'
                              ? 'bg-violet-500 text-white border-violet-500'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Toggles */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600 dark:text-gray-400">Show Calculation</label>
                      <button
                        onClick={handleShowCalculationChange}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          layoutSettings.showCalculation 
                            ? 'bg-violet-500' 
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            layoutSettings.showCalculation ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600 dark:text-gray-400">Show Result</label>
                      <button
                        onClick={handleShowResultChange}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          layoutSettings.showResult 
                            ? 'bg-violet-500' 
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            layoutSettings.showResult ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600 dark:text-gray-400">Show Jump Value</label>
                      <button
                        onClick={handleShowJumpValueChange}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          layoutSettings.showJumpValue 
                            ? 'bg-violet-500' 
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            layoutSettings.showJumpValue ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-600 dark:text-gray-400">Always Show Signs</label>
                      <button
                        onClick={handleAlwaysShowSignsChange}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          layoutSettings.alwaysShowSigns 
                            ? 'bg-violet-500' 
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            layoutSettings.alwaysShowSigns ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {selectedJumpsCount > 0 && (
            <>
              <button
                onClick={() => dispatch(deselectAllJumps())}
                className="px-1.5 py-0.5 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors whitespace-nowrap"
              >
                Deselect
              </button>
              <button
                onClick={handleDeleteSelected}
                className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors whitespace-nowrap"
              >
                Delete
              </button>
              <button
                onClick={handleDuplicateSelected}
                className="px-1.5 py-0.5 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                Dup
              </button>
              {selectedJumpsCount === 1 && (
                <button
                  onClick={() => {
                    const selectedJump = jumpIndicators.jumps.find(j => j.selected);
                    if (selectedJump) {
                      dispatch(reverseJump(selectedJump.id));
                    }
                  }}
                  className="px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors whitespace-nowrap"
                  title="Reverse jump direction (swap start and end)"
                >
                  Reverse
                </button>
              )}
              <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-0.5" />
              <button
                onClick={() => dispatch(moveSelectedJumpsToLine('primary'))}
                className="px-1.5 py-0.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors whitespace-nowrap"
                title="Move selected jumps to primary line"
              >
                To Prim
              </button>
              <button
                onClick={() => dispatch(moveSelectedJumpsToLine('secondary'))}
                className="px-1.5 py-0.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors whitespace-nowrap"
                title="Move selected jumps to secondary line"
              >
                To Sec
              </button>
              <button
                onClick={handleMoveToBoth}
                className="px-1.5 py-0.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors whitespace-nowrap"
                title="Duplicate selected jumps to both lines"
              >
                To Both
              </button>
            </>
          )}
          <button
            onClick={() => dispatch(clearJumps())}
            disabled={jumpIndicators.jumps?.length === 0}
            className="px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
};

// Measurement Toolbar Component
const MeasurementToolbar: React.FC = () => {
  const dispatch = useDispatch();
  const measurement = useSelector((state: RootState) => state.ui?.measurement || { measurements: [] });
  const selectedCount = measurement.measurements?.filter(m => m.selected).length || 0;

  const handleDeleteSelected = () => {
    // Remove selected measurements and also ensure there is no in‑progress measurement
    dispatch(deleteSelectedMeasurements());
    dispatch(cancelMeasurement());
  };

  const handleClearAll = () => {
    // Clear all saved measurements and reset any transient measurement state
    dispatch(clearAllMeasurements());
    dispatch(cancelMeasurement());
  };

  return (
    <div className="context-toolbar p-4 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Left side: Title and count */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M7 21l10-10M7 7l10 10" />
            </svg>
            <span className="font-medium text-green-900 dark:text-green-100">Measurement Tool</span>
          </div>
          
          <div className="text-sm text-green-700 dark:text-green-300">
            {measurement.measurements?.length || 0} total • {selectedCount} selected
          </div>
        </div>

        {/* Center: Tips */}
        <div className="text-xs text-green-600 dark:text-green-400">
          💡 Hold Alt whilst clicking for precise positioning
        </div>

        {/* Right side: Actions */}
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="px-2 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Delete Selected
            </button>
          )}
          <button
            onClick={handleClearAll}
            disabled={measurement.measurements?.length === 0}
            className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
};

// Expression Toolbar Component
const ExpressionToolbar: React.FC = () => {
  const dispatch = useDispatch();
  const expressions = useSelector((state: RootState) => state.expressions || { objects: [], environment: {} });
  const environment = useSelector(selectExpressionEnvironment);
  const selectedObjects = useSelector(selectSelectedExpressionObjects);
  const [showVariablesModal, setShowVariablesModal] = useState(false);

  const handleNewExpression = () => {
    try {
      // Open expression widget at center of screen
      dispatch(showExpressionWidget({
        position: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
        mode: 'create',
        expression: '',
      }));
    } catch (error) {
      console.error('Error opening expression editor:', error);
    }
  };

  const handleDeleteSelected = () => {
    dispatch(deleteSelectedExpressionObjects());
  };

  const handleClearAll = () => {
    dispatch(clearAllExpressions());
  };

  const selectedCount = selectedObjects.length;

  return (
    <>
      <div className="context-toolbar p-4 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Left side: Title and count */}
          <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="font-medium text-purple-900 dark:text-purple-100">Mathematical Expressions</span>
            </div>
            
            <div className="text-sm text-purple-700 dark:text-purple-300">
              {expressions.objects?.length || 0} total • {selectedCount} selected
            </div>
          </div>

          {/* Center: Tips */}
          <div className="text-xs text-purple-600 dark:text-purple-400">
            💡 Right-click on number line to add midpoints
          </div>

          {/* Right side: Actions */}
          <div className="flex items-center gap-2">
            {selectedCount > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="px-2 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Delete Selected
              </button>
            )}
            <button
              onClick={handleClearAll}
              disabled={expressions.objects?.length === 0}
              className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowVariablesModal(true)}
              className="px-3 py-0.5 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            >
              Variables ({Object.keys(environment).length})
            </button>
            
            <button
              onClick={handleNewExpression}
              className="px-3 py-0.5 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
            >
              New Expression
            </button>
          </div>
        </div>
      </div>

      {/* Variables Modal */}
      <VariablesModal 
        isOpen={showVariablesModal} 
        onClose={() => setShowVariablesModal(false)} 
      />
    </>
  );
};



// Main Context Toolbar Component
interface ContextToolbarProps {
  className?: string;
}

// Pan Toolbar Component
const PanToolbar: React.FC = () => {
  const dispatch = useDispatch();
  const ui = useSelector((state: RootState) => state.ui);
  const equivalentFractions = useSelector((state: RootState) => state.equivalentFractions);
  const preferences = useSelector((state: RootState) => state.preferences);
  const viewport = useSelector((state: RootState) => state.viewport);
  const sparseModeEnabled = useSelector((state: RootState) => state.dnlTools?.sparseMode?.enabled ?? false);
  const [jumpSize, setJumpSize] = useState(preferences.defaultJumpSize);
  const [jumpSizeInput, setJumpSizeInput] = useState(preferences.defaultJumpSize.toString());
  const JUMP_STEP = 0.5;
  const isVertical = viewport.orientation === 'vertical';
  const subdivisionModeOptions = useMemo(
    () => [
      { value: 'smart' as SubdivisionMode, label: 'Smart (auto)' },
      { value: 'binary' as SubdivisionMode, label: 'Binary (1/2, 1/4, 1/8)' },
      { value: 'decimal' as SubdivisionMode, label: 'Decimal (10ths, 100ths)' },
      { value: 'halves-tenths' as SubdivisionMode, label: 'Halves → Tenths' },
    ],
    []
  );
  const sessionSubdivisionMode = ui.tickSubdivisionMode;
  const preferenceSubdivisionMode = preferences.subdivisionMode ?? 'smart';
  const subdivisionSelectValue = sessionSubdivisionMode ?? '__default';
  const defaultSubdivisionLabel =
    subdivisionModeOptions.find((opt) => opt.value === preferenceSubdivisionMode)?.label ||
    preferenceSubdivisionMode;
  const isEquivalentMode = equivalentFractions?.mode === 'equivalentFractions';
  const showIntegerLine = ui.showEquivalentIntegerLine !== false;

  // Update jump size when preferences change
  useEffect(() => {
      setJumpSize(preferences.defaultJumpSize);
      setJumpSizeInput(preferences.defaultJumpSize.toString());
  }, [preferences.defaultJumpSize]);

  const handleCenterOrigin = () => {
    // Get the actual viewport dimensions from the number line container
    const numberLineContainer = document.querySelector('[data-numberline-container]');
    const rect = numberLineContainer?.getBoundingClientRect();
    const viewportWidth = rect?.width || 800;
    const viewportHeight = rect?.height || 400;
    dispatch(centerOrigin({ width: viewportWidth, height: viewportHeight }));
  };

  const handleToggleAnnotationPanning = () => {
    dispatch(toggleAnnotationLayerPanning());
  };

  const handleJumpBackward = () => {
    // Pan backward/down by jump size (to show lower numbers)
    const pixelAmount = jumpSize * viewport.scale;
    if (isVertical) {
      dispatch(pan({ deltaY: pixelAmount }));
    } else {
      dispatch(pan(pixelAmount));
    }
  };

  const handleJumpForward = () => {
    // Pan forward/up by jump size (to show higher numbers)
    const pixelAmount = jumpSize * viewport.scale;
    if (isVertical) {
      dispatch(pan({ deltaY: -pixelAmount }));
    } else {
      dispatch(pan(-pixelAmount));
    }
  };

  const handleJumpSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setJumpSizeInput(inputValue);
    
    const value = parseFloat(inputValue);
    if (!isNaN(value) && value > 0) {
      setJumpSize(value);
    }
  };

  const handleJumpSizeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value <= 0) {
      const fallback = preferences.defaultJumpSize;
      setJumpSize(fallback);
      setJumpSizeInput(fallback.toString());
    } else {
      setJumpSizeInput(value.toString());
    }
  };

  const handleToggleAllPrimaryLabels = () => {
    dispatch(toggleMainLabelsHidden());
  };

  const handleToggleIntegerLine = () => {
    dispatch(setShowEquivalentIntegerLine(!showIntegerLine));
  };

  const handleSubdivisionModeChange = (value: string) => {
    if (value === '__default') {
      dispatch(setTickSubdivisionMode(undefined));
    } else {
      dispatch(setTickSubdivisionMode(value as SubdivisionMode));
    }
  };

  const handleOpenAddPointDialog = () => {
    dispatch(openAddPointDialog({ targetLine: 'primary' }));
  };

  const clampJump = (value: number) => Math.max(0.1, Math.min(100, value));

  const handleIncrementJumpSize = () => {
    const current = parseFloat(jumpSizeInput);
    const base = Number.isFinite(current) && current > 0 ? current : jumpSize;
    const next = clampJump(parseFloat((base + JUMP_STEP).toFixed(3)));
    setJumpSize(next);
    setJumpSizeInput(next.toString());
  };

  const handleDecrementJumpSize = () => {
    const current = parseFloat(jumpSizeInput);
    const base = Number.isFinite(current) && current > 0 ? current : jumpSize;
    const next = clampJump(parseFloat((base - JUMP_STEP).toFixed(3)));
    setJumpSize(next);
    setJumpSizeInput(next.toString());
  };

  return (
    <div className="context-toolbar p-4 bg-gray-50 dark:bg-gray-900/20 border-b border-gray-200 dark:border-gray-800">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Left side: Title */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M7 11.5l5-5 5 5M7 19.5l5-5 5 5" />
            </svg>
            <span className="font-medium text-gray-900 dark:text-gray-100">Pan</span>
          </div>
        </div>

        {/* Center: Tips */}
        <div className="text-xs text-gray-600 dark:text-gray-400">
          💡 Drag to pan • Ctrl+Scroll to zoom
        </div>

        {/* Right side: Controls */}
        <div className="flex items-center gap-2">
          {/* Tick subdivision mode (session-only) - hidden in equivalent fractions mode */}
          {!isEquivalentMode && (
            <div className="flex items-center gap-2 mr-2">
              <span className="text-xs text-gray-600 dark:text-gray-400">Tick mode</span>
              <select
                value={subdivisionSelectValue}
                onChange={(e) => handleSubdivisionModeChange(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                title="Choose tick subdivision mode for this session"
              >
                <option value="__default">{`Default (${defaultSubdivisionLabel})`}</option>
                {subdivisionModeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {/* Custom Point Button - only shown when sparse mode is enabled */}
              {sparseModeEnabled && (
                <button
                  onClick={handleOpenAddPointDialog}
                  className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors flex items-center gap-1"
                  title="Add custom point to the number line"
                >
                  <span>📍</span>
                  <span>Add Point</span>
                </button>
              )}
            </div>
          )}
          {/* Equivalent fractions integer line toggle */}
          {isEquivalentMode && (
            <div className="flex items-center gap-2 mr-2">
              <span className="text-xs font-bold text-gray-600 dark:text-gray-400">Integer line:</span>
              <button
                onClick={handleToggleIntegerLine}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showIntegerLine ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                title={showIntegerLine ? 'Hide integer-only helper line' : 'Show integer-only helper line'}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showIntegerLine ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-xs text-gray-600 dark:text-gray-400">Show</span>
            </div>
          )}
          {/* Main labels toggle */}
          <div className="flex items-center gap-2 mr-4">
            <button
              onClick={handleToggleAllPrimaryLabels}
              className="px-3 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              title="Toggle all main number line labels on/off"
            >
              Toggle Labels
            </button>
          </div>
          {/* Annotation Panning Toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-600 dark:text-gray-400">Annotations:</span>
            <span className="text-xs text-gray-600 dark:text-gray-400">Fixed</span>
            <button
              onClick={handleToggleAnnotationPanning}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                ui.annotationLayer?.panWithNumberLine !== false
                  ? 'bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={ui.annotationLayer?.panWithNumberLine !== false ? 'Annotations pan with number line' : 'Annotations stay fixed'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  ui.annotationLayer?.panWithNumberLine !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-xs text-gray-600 dark:text-gray-400">Pan with line</span>
          </div>

          {/* Jump Controls */}
          <div className="flex items-center gap-1 ml-4">
            {/* Separator */}
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mr-3"></div>
            {/* Jump size input and steppers */}
            <div className="flex items-center gap-1 mr-2" title="Set jump size (units)">
              <button
                onClick={handleDecrementJumpSize}
                className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                aria-label="Decrease jump size"
              >
                −
              </button>
              <input
                type="number"
                inputMode="decimal"
                step={JUMP_STEP}
                min={0.1}
                max={100}
                value={jumpSizeInput}
                onChange={handleJumpSizeChange}
                onBlur={handleJumpSizeBlur}
                className="w-16 px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-center"
                aria-label="Jump size in units"
              />
              <button
                onClick={handleIncrementJumpSize}
                className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                aria-label="Increase jump size"
              >
                +
              </button>
            </div>
            
            {/* Jump backward button */}
            <button
              onClick={handleJumpBackward}
              className="px-2 py-0.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title={`${isVertical ? 'Jump down' : 'Jump backward'} by ${jumpSize} units`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isVertical ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                )}
              </svg>
            </button>
            <button
              onClick={handleJumpForward}
              className="p-0.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
              title={`${isVertical ? 'Jump up' : 'Jump forward'} by ${jumpSize} units`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isVertical ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                )}
              </svg>
            </button>
          </div>

          <button
            onClick={handleCenterOrigin}
            className="px-3 py-0.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            title="Centre the origin (0) in the viewport"
          >
            Centre Origin
          </button>
        </div>
      </div>
    </div>
  );
};

export const ContextToolbar: React.FC<ContextToolbarProps> = ({ className = '' }) => {
  const activeTool = useSelector(selectActiveTool);
  const annotations = useSelector((state: RootState) => state.annotations);

  // Calculate annotation counts
  const totalAnnotations = (annotations.shapes?.length || 0) + (annotations.texts?.length || 0) + (annotations.strokes?.length || 0);
  const selectedAnnotations = (annotations.shapes?.filter(s => s.selected).length || 0) + 
                             (annotations.texts?.filter(t => t.selected).length || 0) + 
                             (annotations.strokes?.filter(st => st.selected).length || 0);

  // Don't render anything if no active tool
  if (!activeTool) return null;

  return (
    <div className={`context-toolbar-container ${className}`}>
      {activeTool === 'pan' && <PanToolbar />}
      {activeTool === 'jump' && <JumpToolbar />}
      {activeTool === 'measurement' && <MeasurementToolbar />}
      {activeTool === 'annotation' && (
        <div className="context-toolbar p-4 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Left side: Title and count */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span className="font-medium text-orange-900 dark:text-orange-100">Annotation</span>
              </div>
              
              <div className="text-sm text-orange-700 dark:text-orange-300">
                {totalAnnotations} total • {selectedAnnotations} selected
              </div>
            </div>

            {/* Center: Annotation toolbar */}
            <div className="flex items-start">
              <AnnotationToolbar visible={true} orientation="horizontal" />
            </div>

            {/* Right side: Empty for now */}
            <div className="flex items-center gap-2">
            </div>
          </div>
        </div>
      )}
      {activeTool === 'expression' && <ExpressionToolbar />}
    </div>
  );
};

export default ContextToolbar;